// src/components/empresas/ConfigTributariaPage/ConfigTributariaPresentation.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Container, Typography, Box, Button, Grid, TextField,
  RadioGroup, FormControlLabel, Radio, FormLabel, FormControl,
  CircularProgress, Chip, Paper, Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const ConfigTributariaPresentation = ({
  empresa,
  rutEmpresa,
  formData,
  formErrors,
  loading,
  saving,
  configurado,
  onInputChange,
  onSubmit,
  onVolver,
}) => {
  const razonSocial = empresa?.razon_social || empresa?.nombre_corto || rutEmpresa;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onVolver} variant="outlined" size="small">
          Volver
        </Button>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" fontWeight={600}>
              Config Tributaria
            </Typography>
            <Chip
              label={configurado ? 'Configurado' : 'Sin configurar'}
              color={configurado ? 'success' : 'warning'}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {razonSocial} — RUT: {rutEmpresa}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={onSubmit}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Datos tributarios
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                label="Razón Social"
                name="razon_social"
                value={formData.razon_social}
                onChange={onInputChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Giro *"
                name="giro"
                value={formData.giro}
                onChange={onInputChange}
                error={!!formErrors.giro}
                helperText={formErrors.giro}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Acteco *"
                name="acteco"
                type="number"
                value={formData.acteco}
                onChange={onInputChange}
                error={!!formErrors.acteco}
                helperText={formErrors.acteco}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Dirección fiscal *"
                name="direccion"
                value={formData.direccion}
                onChange={onInputChange}
                error={!!formErrors.direccion}
                helperText={formErrors.direccion}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Comuna *"
                name="comuna"
                value={formData.comuna}
                onChange={onInputChange}
                error={!!formErrors.comuna}
                helperText={formErrors.comuna}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ciudad"
                name="ciudad"
                value={formData.ciudad}
                onChange={onInputChange}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Resolución SII
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha resolución"
                name="fecha_resolucion_sii"
                type="date"
                value={formData.fecha_resolucion_sii}
                onChange={onInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="N° resolución"
                name="nro_resolucion_sii"
                type="number"
                value={formData.nro_resolucion_sii}
                onChange={onInputChange}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Ambiente DTE
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Seleccione el ambiente</FormLabel>
            <RadioGroup
              row
              name="ambiente_dte"
              value={formData.ambiente_dte}
              onChange={onInputChange}
            >
              <FormControlLabel value="certificacion" control={<Radio size="small" />} label="Certificación" />
              <FormControlLabel value="produccion" control={<Radio size="small" />} label="Producción" />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Código sucursal (opcional)"
                name="codigo_sucursal"
                value={formData.codigo_sucursal}
                onChange={onInputChange}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

ConfigTributariaPresentation.propTypes = {
  empresa: PropTypes.object,
  rutEmpresa: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  formErrors: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  configurado: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onVolver: PropTypes.func.isRequired,
};

export default ConfigTributariaPresentation;
